import { Fragment, useEffect, useState, type ComponentType, type ReactNode } from 'react'
import {
  BookOpen,
  Building2,
  FilePlus2,
  MapPin,
  Shapes,
  Sparkles,
  Users,
  type LucideProps,
} from 'lucide-react'
import { KNOWLEDGE_FACTS_MAX_CHARS, KNOWLEDGE_TOPICS, type KnowledgeTopicId } from '@k4ia/shared'
import {
  createKnowledgeText,
  deleteDocument,
  getConfig,
  listDocumentChunks,
  listDocuments,
  reindexDocument,
  updateConfig,
  updateDocument,
  uploadDocumentFile,
  uploadDocumentUrl,
  type AdminDocument,
} from '../lib/api'
import { ReindexAllButton } from './DocumentsPage'
import { FileButton } from '../components/ui'
import { WebseitenStand } from '../components/WebseitenStand'

/**
 * Alles, was K4ia über die Firma, die Mutterfirma und das Lab wissen soll —
 * unabhängig von einzelnen Showcases. Zwei Ebenen, weil sie verschieden
 * wirken (ADR 0012):
 *
 * - **Grundwissen** steht in jedem Prompt. Kurz, nie nachgeschlagen: Wer wir
 *   sind, wo das Lab ist, wie man jemanden erreicht.
 * - **Einträge** landen in der Wissensbasis und werden bei Bedarf gesucht:
 *   Geschichte, Leistungen, Team, Anfahrt — so lang, wie es sein muss.
 *
 * Einträge sind gewöhnliche Dokumente (FR-02) ohne Showcase, mit einem Thema
 * zur Ordnung. Text kann direkt hier getippt oder eingefügt werden; Dateien
 * und Links gehen denselben Weg wie bei den Showcase-Dokumenten.
 */

const STATUS_LABEL: Record<AdminDocument['status'], string> = {
  uploaded: '⏳ wartet',
  indexing: '⚙️ wird indexiert',
  indexed: '✅ durchsuchbar',
  error: '❌ Fehler',
}

const TOPIC_ICON: Record<KnowledgeTopicId, ComponentType<LucideProps>> = {
  j4f: Building2,
  j4p: Building2,
  visionlab: Sparkles,
  team: Users,
  besuch: MapPin,
  sonstiges: Shapes,
}

const TOPIC_TILE: Record<KnowledgeTopicId, string> = {
  j4f: '#63c3d1',
  j4p: '#17727e',
  visionlab: '#d2436d',
  team: '#f08046',
  besuch: '#047857',
  sonstiges: '#636366',
}

export function KnowledgePage() {
  const [documents, setDocuments] = useState<AdminDocument[]>([])
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    const { documents } = await listDocuments()
    // Nur Wissen ohne Showcase — die Showcase-Dokumente stehen unter „Dokumente".
    setDocuments(documents.filter((d) => d.showcaseId === null))
  }

  useEffect(() => {
    void refresh()
    const interval = setInterval(() => void refresh(), 4000) // Indexierung läuft asynchron
    return () => clearInterval(interval)
  }, [])

  async function run(aktion: () => Promise<unknown>) {
    setError(null)
    try {
      await aktion()
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="max-w-3xl space-y-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <h2 className="page-title">Wissen</h2>
          <p className="text-sm leading-relaxed text-text-muted">
            Alles, was K4ia über Just 4 Fashion, Just 4 People und das VisionLab wissen soll —
            unabhängig von einzelnen Showcases. Änderungen wirken sofort, ohne Deployment.
          </p>
        </div>
        <ReindexAllButton onDone={() => void refresh()} />
      </div>

      {error && (
        <p role="alert" className="text-sm text-status-error">
          {error}
        </p>
      )}

      <FactsSection />
      <NewEntrySection onDone={() => void refresh()} onError={setError} />

      {KNOWLEDGE_TOPICS.map((topic) => (
        <TopicSection
          key={topic.id}
          topic={topic}
          entries={documents.filter((d) => (d.topic ?? 'sonstiges') === topic.id)}
          onRun={run}
        />
      ))}
    </div>
  )
}

/** Grundwissen: der kurze Steckbrief, der in jedem Prompt steht. */
function FactsSection() {
  const [text, setText] = useState('')
  const [gespeichert, setGespeichert] = useState('')
  const [status, setStatus] = useState<'lädt' | 'bereit' | 'speichert' | 'gespeichert'>('lädt')

  useEffect(() => {
    void getConfig().then(({ config }) => {
      setText(config.knowledgeFacts)
      setGespeichert(config.knowledgeFacts)
      setStatus('bereit')
    })
  }, [])

  const dirty = text !== gespeichert
  const zuLang = text.length > KNOWLEDGE_FACTS_MAX_CHARS

  async function save() {
    setStatus('speichert')
    const { config } = await updateConfig({ knowledgeFacts: text.trim() })
    setText(config.knowledgeFacts)
    setGespeichert(config.knowledgeFacts)
    setStatus('gespeichert')
  }

  return (
    <Section
      title="Grundwissen — steht in jeder Antwort"
      icon={BookOpen}
      tile="#1d1d1f"
      footer="Kurz halten: Das hier liest K4ia bei jeder einzelnen Antwort mit. Was länger ist oder nur manchmal gebraucht wird, gehört unten als Eintrag in die Wissensbasis — dort schlägt sie gezielt nach."
    >
      <div className="space-y-3 px-5 py-4">
        <textarea
          className="input min-h-40 font-mono leading-relaxed md:text-xs"
          rows={8}
          value={text}
          disabled={status === 'lädt'}
          onChange={(e) => {
            setText(e.target.value)
            setStatus('bereit')
          }}
          placeholder={
            'Just 4 Fashion (J4F) ist … und gehört zu Just 4 People (j4p).\n' +
            'Das J4F VisionLab ist der Showroom von J4F in …\n' +
            'Öffnungszeiten: … Ansprechpartner vor Ort: …'
          }
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className={`font-mono text-xs ${zuLang ? 'text-status-error' : 'text-text-muted'}`}>
            {text.length} / {KNOWLEDGE_FACTS_MAX_CHARS} Zeichen
          </span>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {status === 'gespeichert' && !dirty && (
              <span className="text-xs text-status-active">Gespeichert — gilt ab jetzt</span>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={!dirty || zuLang || status === 'speichert' || status === 'lädt'}
              onClick={() => void save()}
            >
              {status === 'speichert' ? 'Speichert …' : 'Grundwissen speichern'}
            </button>
          </div>
        </div>
      </div>
    </Section>
  )
}

/** Neuer Eintrag: Text tippen oder einfügen, Datei hochladen oder Link angeben. */
function NewEntrySection({
  onDone,
  onError,
}: {
  onDone: () => void
  onError: (message: string | null) => void
}) {
  const [topic, setTopic] = useState<KnowledgeTopicId>('j4f')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)

  async function run(aktion: () => Promise<unknown>) {
    setBusy(true)
    onError(null)
    try {
      await aktion()
      setTitle('')
      setBody('')
      setUrl('')
      onDone()
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section
      title="Neuer Eintrag"
      icon={FilePlus2}
      tile="#63c3d1"
      glyph="#1d1d1f"
      footer={
        'Text wird in Abschnitte zerteilt und durchsuchbar gemacht; das dauert ein paar Sekunden. Dateien: PDF, Word, PowerPoint, Markdown, Text. Links: K4ia liest die Seite ein (Menü und Fußzeile bleiben weg) und sieht jede Nacht nach, ob sie sich geändert hat.'
      }
    >
      <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium text-text-muted">Thema</span>
          <select
            className="input"
            value={topic}
            onChange={(e) => setTopic(e.target.value as KnowledgeTopicId)}
          >
            {KNOWLEDGE_TOPICS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium text-text-muted">Titel</span>
          <input
            className="input"
            placeholder="z. B. Geschichte von J4F, Öffnungszeiten, Team"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
      </div>

      <div className="space-y-3 px-5 py-4">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-text-muted">Text tippen oder einfügen</span>
          <textarea
            className="input min-h-32"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Fließtext, Aufzählungen, Fragen und Antworten — so, wie es jemand aus dem Team erklären würde."
          />
        </label>
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            disabled={busy || !title.trim() || !body.trim()}
            onClick={() =>
              void run(() => createKnowledgeText({ title: title.trim(), topic, body }))
            }
          >
            Text speichern
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
        <span className="text-xs font-medium text-text-muted sm:w-24 sm:shrink-0">oder Datei</span>
        <FileButton
          label="Datei hochladen"
          accept=".pdf,.docx,.pptx,.md,.markdown,.txt"
          disabled={busy}
          hint="PDF, Word, PowerPoint, Markdown, Text"
          onFile={(file) =>
            run(() => uploadDocumentFile(file, title.trim() || undefined, undefined, topic))
          }
        />
      </div>

      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
        <span className="text-xs font-medium text-text-muted sm:w-24 sm:shrink-0">oder Link</span>
        <input
          className="input"
          type="url"
          inputMode="url"
          placeholder="https://www.just4fashion.de/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={busy || !url.trim()}
          onClick={() =>
            void run(() =>
              uploadDocumentUrl(url.trim(), title.trim() || undefined, undefined, topic),
            )
          }
        >
          Link hinzufügen
        </button>
      </div>
    </Section>
  )
}

function TopicSection({
  topic,
  entries,
  onRun,
}: {
  topic: (typeof KNOWLEDGE_TOPICS)[number]
  entries: AdminDocument[]
  onRun: (aktion: () => Promise<unknown>) => Promise<void>
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [chunksFor, setChunksFor] = useState<string | null>(null)
  const [chunks, setChunks] = useState<{ id: string; text: string; position: number }[]>([])

  async function toggleChunks(id: string) {
    if (chunksFor === id) {
      setChunksFor(null)
      return
    }
    const { chunks } = await listDocumentChunks(id)
    setChunks(chunks)
    setChunksFor(id)
  }

  return (
    <Section title={topic.label} icon={TOPIC_ICON[topic.id]} tile={TOPIC_TILE[topic.id]}>
      {entries.length === 0 && (
        <p className="px-5 py-4 text-sm text-text-muted">Noch nichts hinterlegt — {topic.hint}.</p>
      )}
      {entries.map((doc) => (
        <Fragment key={doc.id}>
          <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-[0.95rem] font-medium sm:truncate">{doc.title}</p>
              <p className="mt-0.5 text-xs text-text-muted">
                <KindBadge doc={doc} /> · {STATUS_LABEL[doc.status]}
                {doc.status === 'error' && doc.errorMessage && (
                  <span className="ml-1 text-status-error">({doc.errorMessage})</span>
                )}
                {doc.body === null && doc.source !== doc.title && (
                  <span className="ml-1 break-all">· {doc.source}</span>
                )}
              </p>
              <WebseitenStand doc={doc} onDone={() => onRun(() => Promise.resolve())} />
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {doc.body !== null && (
                <button
                  type="button"
                  className="btn-chip"
                  onClick={() => setEditing(editing === doc.id ? null : doc.id)}
                >
                  {editing === doc.id ? 'Schließen' : 'Bearbeiten'}
                </button>
              )}
              <button type="button" className="btn-chip" onClick={() => void toggleChunks(doc.id)}>
                Abschnitte
              </button>
              <button
                type="button"
                className="btn-chip"
                onClick={() => void onRun(() => reindexDocument(doc.id))}
              >
                Neu indexieren
              </button>
              <button
                type="button"
                className="btn-chip-danger"
                onClick={() => {
                  if (
                    window.confirm(`„${doc.title}" löschen? K4ia kennt den Inhalt dann nicht mehr.`)
                  ) {
                    void onRun(() => deleteDocument(doc.id))
                  }
                }}
              >
                Löschen
              </button>
            </div>
          </div>
          {editing === doc.id && doc.body !== null && (
            <EditEntry
              doc={doc}
              onSaved={() => {
                setEditing(null)
              }}
              onRun={onRun}
            />
          )}
          {chunksFor === doc.id && (
            <ol className="list-decimal space-y-2 bg-bg-elevated px-5 py-3 pl-10 text-xs text-text-muted">
              {chunks.length === 0 && <li className="list-none -ml-5">Noch keine Abschnitte.</li>}
              {chunks.map((c) => (
                <li key={c.id} className="whitespace-pre-wrap">
                  {c.text}
                </li>
              ))}
            </ol>
          )}
        </Fragment>
      ))}
    </Section>
  )
}

function EditEntry({
  doc,
  onSaved,
  onRun,
}: {
  doc: AdminDocument
  onSaved: () => void
  onRun: (aktion: () => Promise<unknown>) => Promise<void>
}) {
  const [title, setTitle] = useState(doc.title)
  const [topic, setTopic] = useState<KnowledgeTopicId>(
    (doc.topic as KnowledgeTopicId | null) ?? 'sonstiges',
  )
  const [body, setBody] = useState(doc.body ?? '')
  const dirty = title !== doc.title || topic !== (doc.topic ?? 'sonstiges') || body !== doc.body

  return (
    <div className="space-y-3 bg-bg-elevated px-5 py-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <select
          className="input"
          value={topic}
          onChange={(e) => setTopic(e.target.value as KnowledgeTopicId)}
        >
          {KNOWLEDGE_TOPICS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <textarea
        className="input min-h-40"
        rows={10}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs text-text-muted">
          Ein geänderter Text wird neu indexiert; Titel und Thema gelten sofort.
        </span>
        <button
          type="button"
          className="btn-primary shrink-0"
          disabled={!dirty || !title.trim() || !body.trim()}
          onClick={() =>
            void onRun(() => updateDocument(doc.id, { title: title.trim(), topic, body })).then(
              onSaved,
            )
          }
        >
          Speichern
        </button>
      </div>
    </div>
  )
}

/** Text, Datei oder Link — im Wort, nicht nur im Symbol. */
function KindBadge({ doc }: { doc: AdminDocument }) {
  const art = doc.body !== null ? 'Text' : doc.r2Key ? 'Datei' : 'Link'
  return (
    <span className="rounded-full bg-surface-2 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider">
      {art}
    </span>
  )
}

/** Gleiche Form wie auf der Konfigurationsseite: Kachel mit Symbol, Titel, Karte. */
function Section({
  title,
  icon: Icon,
  tile,
  glyph = '#ffffff',
  footer,
  children,
}: {
  title: string
  icon: ComponentType<LucideProps>
  tile: string
  glyph?: string
  footer?: string
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3 px-1">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-inset ring-border"
          style={{ background: tile, color: glyph }}
        >
          <Icon size={18} strokeWidth={2.25} />
        </span>
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="card divide-y divide-border overflow-hidden">{children}</div>
      {footer && <p className="px-5 text-xs leading-relaxed text-text-muted">{footer}</p>}
    </section>
  )
}
